import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { useLoggedInEmployeeId } from '~/components/Auth';
import { EmployeeCollaborationTaskStatus } from '~/data/collaboration';
import { EnumListKey, useAllEnums } from '~/data/enums';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import { submitEmployeeWorkListTask } from '~/scenes/PeopleModule/components/Tabs/CollaborationTab/WorklistTable/data';
import {
  WorklistTaskFormData,
  convertEditTaskDataToSubmitPayload,
} from '~/scenes/PeopleModule/components/Tabs/CollaborationTab/WorklistTable/EditWorklistTaskDialog/data';
import { useWorklistTaskValidation } from '~/scenes/PeopleModule/components/Tabs/CollaborationTab/WorklistTable/EditWorklistTaskDialog/useWorklistTaskValidation';

export type UseEditWorklistTaskDialogProps = {
  employeeId: number;
  selectedTask?: WorklistTaskFormData;
  onClose: () => void;
  onProcessSubmission?: (data: WorklistTaskFormData) => Promise<void>;
  onUpdate?: () => void;
};

export const useEditWorklistTaskDialog = (
  props: UseEditWorklistTaskDialogProps,
) => {
  const { selectedTask, employeeId, onClose, onProcessSubmission, onUpdate } =
    props;

  const { getEnums } = useAllEnums();
  const taskOptions = useMemo(() => {
    return {
      type: getEnums(EnumListKey.COLLAB_TASK_TYPE),
      status: getEnums(EnumListKey.COLLAB_TASK_STATUS),
    };
  }, [getEnums]);

  const loggedInEmployeeId = useLoggedInEmployeeId();

  const emptyFormData: WorklistTaskFormData = useMemo(() => {
    return {
      title: '',
      description: '',
      targetEmployeeId: employeeId,
      creatorEmployeeId: loggedInEmployeeId,
      taskType: '',
      taskStatus: EmployeeCollaborationTaskStatus.New,
      attachmentId: -1,
      attachmentName: '',
      attachmentIsArchived: false,
      link: '',
    };
  }, [employeeId, loggedInEmployeeId]);

  const { validationSchema } = useWorklistTaskValidation();

  const resolver = useYupValidationResolver(
    validationSchema,
    'react-hook-form',
  );

  const form = useForm<WorklistTaskFormData>({
    resolver,
    defaultValues: emptyFormData,
  });
  const { reset, handleSubmit } = form;

  useEffect(() => {
    reset(!!selectedTask ? selectedTask : emptyFormData);
  }, [selectedTask, reset, emptyFormData]);

  const onCloseDialog = useCallback(() => {
    reset(emptyFormData);
    onClose();
  }, [reset, emptyFormData, onClose]);

  const onSubmit = useMemo(() => {
    return handleSubmit((data) => {
      if (!!onProcessSubmission) {
        onProcessSubmission(data).then(() => {
          onCloseDialog();
        });
      } else {
        const payload = convertEditTaskDataToSubmitPayload(data);

        if (!payload.fTaskId) {
          return Promise.reject('No Task Id');
        }

        submitEmployeeWorkListTask(payload)
          .then(() => {
            if (!!onUpdate) {
              onUpdate();
              onCloseDialog();
            }
          })
          .catch((err) => {
            console.log(err);
          });
      }
    });
  }, [handleSubmit, onProcessSubmission, onUpdate, onCloseDialog]);

  const { disabledFields } = useMemo(() => {
    const isTemplateTask = !!selectedTask?.templateId;
    const isWorkListTask = !!selectedTask?.worklistId;

    return {
      disabledFields: {
        title: isTemplateTask,
        description: isTemplateTask,
        taskType: isTemplateTask || isWorkListTask,
        targetEmployeeId: isTemplateTask || isWorkListTask,
        link: isTemplateTask,
      },
      visibleFields: {},
    };
  }, [selectedTask]);

  return {
    form,
    disabledFields,
    onCloseDialog,
    onSubmit,
    taskOptions,
  };
};
